import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Card } from "@/components/ui/card";
import { formatINR } from "@/lib/format";

const day = (v) => (v || "").slice(0, 10);
const label = (d) => { const x = new Date(d); return isNaN(x) ? d : x.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); };

export function ratePoints(r) {
  const pts = (r.rate_history || []).map((h) => ({ date: day(h.effective_date) || day(h.changed_at), amount: Number(h.amount) || 0 }));
  pts.push({ date: day(r.effective_date) || day(r.updated_at), amount: Number(r.amount) || 0 });
  return pts.filter((p) => p.date && p.amount > 0).sort((a, b) => a.date.localeCompare(b.date));
}

function TrendCard({ rate }) {
  const pts = ratePoints(rate);
  const last = pts[pts.length - 1]?.amount || 0;
  const prev = pts.length > 1 ? pts[pts.length - 2].amount : null;
  const diff = prev ? last - prev : 0;
  const pct = prev ? (diff / prev) * 100 : 0;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const color = diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : "text-muted-foreground";
  const slug = rate.linked_material_type.replace(/\s+/g, "-").toLowerCase();
  return (
    <Card className="p-4" data-testid={`rate-trend-${slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{rate.linked_material_type}</div>
          <div className="font-display text-xl font-bold" data-testid={`rate-trend-current-${slug}`}>{formatINR(last)}{rate.unit && <span className="text-xs font-normal text-muted-foreground"> /{rate.unit}</span>}</div>
        </div>
        <div className={`text-xs font-medium inline-flex items-center gap-1 ${color}`} data-testid={`rate-trend-change-${slug}`}>
          <Icon className="h-3.5 w-3.5" />{prev ? `${diff > 0 ? "+" : ""}${pct.toFixed(1)}%` : "First sale"}
        </div>
      </div>
      <div className="h-24 mt-2">
        {pts.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pts} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="date" tickFormatter={label} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={16} />
              <YAxis hide domain={["dataMin", "dataMax"]} />
              <Tooltip formatter={(v) => [formatINR(v), "Sold rate"]} labelFormatter={label} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Line type="monotone" dataKey="amount" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Trend appears after the next sale.</div>}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{pts.length} sale{pts.length !== 1 ? "s" : ""} · last {label(pts[pts.length - 1]?.date)}</div>
    </Card>
  );
}

export function RateTrendChart({ rates }) {
  const linked = rates.filter((r) => r.linked_material_type).sort((a, b) => a.linked_material_type.localeCompare(b.linked_material_type));
  return (
    <div className="space-y-2" data-testid="rate-trend-section">
      <div className="text-sm font-medium text-muted-foreground">Sold Rate Trend (from Sold Material / Scrap)</div>
      {linked.length === 0 ? (
        <Card className="p-4 text-sm text-muted-foreground" data-testid="rate-trend-empty">No sold rates yet. Save a Sold Material / Scrap entry with a sold rate to start the trend.</Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{linked.map((r) => <TrendCard key={r.id} rate={r} />)}</div>
      )}
    </div>
  );
}
